/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IImageData, Image, ICoreContext } from '@msdyn365-commerce/core';
import { ISearchData, ISearchProductViewProps, ISearchProductItemViewProps } from '@msdyn365-commerce-modules/search';
import { INodeProps, Node } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { ISearchProps } from '../../definition-extensions/search.ext.props.autogenerated';
import get from 'lodash/get';

export interface ISearchProductItemExtensionProps {
    uomId: number;
    uomPriceUnitProp: number;
    uomDescProp: string;
    surchargeAmount: number;
    surchargeFactor: number;
    isSurchargePercent: boolean;
    surchargeLabelResource: string;
}
/**
 * Render Products Suggestion result heading.
 * @param productSuggestionsHeading -The products suggestion result heading.
 * @returns The JSX Element.
 */
const renderProductSuggestionsTitle = (productSuggestionsHeading: string): JSX.Element => {
    const productAutoSuggestionHeading = productSuggestionsHeading ? productSuggestionsHeading : 'Products';

    return <div className='msc-autoSuggest__productResults-title'>{productAutoSuggestionHeading}</div>;
};

/**
 * Render Products Suggestion result heading.
 * @param props -The props.
 * @returns The JSX Element.
 */
const renderProductNotFoundResultsImage = (props: ISearchProps<ISearchData>): JSX.Element | null => {
    const noSearchResultImage: IImageData | undefined = props.config.noSearchResultImage;
    if (!noSearchResultImage || !noSearchResultImage.src) {
        return null;
    }
    return (
        <Image
            altText={noSearchResultImage.altText}
            className='msc-autoSuggest__productResults-no-results-image-img'
            gridSettings={props.context.request.gridSettings!}
            src={noSearchResultImage.src}
        />
    );
};

/**
 * Render Products No result section.
 * @param props -The props.
 * @returns The JSX Element.
 */
const renderProductNotFoundResults = (props: ISearchProps<ISearchData>): JSX.Element => {
    return (
        <Node className='msc-autoSuggest__productResults-no-results-content'>
            <div className='msc-autoSuggest__productResults-no-results-image'>{renderProductNotFoundResultsImage(props)}</div>
            <div className='msc-autoSuggest__productResults-no-results-content-title'>{props.resources.noResultContentHeadingText}</div>
            <div className='msc-autoSuggest__productResults-no-results-content-paragraph'>
                {props.resources.noResultContentParagraphText}
            </div>
        </Node>
    );
};

function renderUomPrice(context: ICoreContext, item: ISearchProductItemViewProps, uomConversionData: ISearchProductItemExtensionProps | undefined) {
    if (!item || !uomConversionData) {
        return <></>;
    }

    const basePrice = get(item, 'price.props.data.price.BasePrice');
    if (!basePrice || !uomConversionData) {
        return <></>;
    }

    const formatAmount = (context: ICoreContext, amount: number | undefined, currencyCode?: string | undefined) => {
        if (amount === undefined) {
            return '';
        }
        return currencyCode
            ? context.cultureFormatter.formatCurrency(amount, currencyCode)
            : context.cultureFormatter.formatCurrency(amount);
    };

    const calculateSurchargePerDefaultUnit = () => {
        const ecommSurchargeAmount = uomConversionData.surchargeAmount;
        const ecommSurchargeFactor = uomConversionData.surchargeFactor;
        const isEcommSurchargePercent = uomConversionData.isSurchargePercent;

        if (ecommSurchargeAmount && ecommSurchargeFactor) {
            if (isEcommSurchargePercent) {
                return basePrice * (ecommSurchargeAmount / 100);
            } else {
                return ecommSurchargeAmount / ecommSurchargeFactor;
            }
        }
        return 0;
    };
    const surchargeAmountPerDefaultUnit = calculateSurchargePerDefaultUnit();
    const unitMultipliedSurchargeAmount = uomConversionData.uomPriceUnitProp
        ? uomConversionData.uomPriceUnitProp * surchargeAmountPerDefaultUnit
        : surchargeAmountPerDefaultUnit;

    const unitMultipliedPrice = uomConversionData.uomPriceUnitProp ? uomConversionData.uomPriceUnitProp * basePrice : basePrice;

    const surchargeLabel: string | null = !!unitMultipliedSurchargeAmount ? `${uomConversionData.surchargeLabelResource} ${formatAmount(context, unitMultipliedSurchargeAmount)}` : null;
    const pricePerText = `Price per`;
    // uomDescProp && uomDescProp.toLocaleLowerCase().includes('per')
    //     ? 'Price' // resources.pricePerLabelText.toLocaleLowerCase().replace('per', '')
    //     : 'Price Per'; // resources.pricePerLabelText;

    let unitNumDescriptor = '';
    if (uomConversionData.uomDescProp && uomConversionData.uomPriceUnitProp === 1000) {
        unitNumDescriptor = 'Thousand'; //resources.thousandText;
    } else if (uomConversionData.uomPriceUnitProp && uomConversionData.uomPriceUnitProp > 1) {
               unitNumDescriptor = uomConversionData.uomPriceUnitProp.toString();
    }
    return (
        <>
            <span className='msc-price msc-autoSuggest__productResults-unit-price-text'>
                {pricePerText} {unitNumDescriptor} {uomConversionData.uomDescProp}
            </span>
            <span className='msc-price msc-autoSuggest__productResults-unit-price'>
                {formatAmount(context, (unitMultipliedSurchargeAmount + unitMultipliedPrice))}
            </span>
            <span className='msc-price msc-autoSuggest__productResults-unit-price-text'>
                {surchargeLabel}
            </span>
        </>
    );
}

/**
 * Render Product suggestion result.
 * @param productSuggest -The product suggest Node props.
 * @param ulProduct -The List Node props.
 * @param props -The props.
 * @param productSuggestions -The Product suggestion result.
 * @param isLoadingAutoSuggest - The autosuggestion loading flag.
 * @param isLoadingNode - The autosuggestion loading node.
 * @returns The JSX Element.
 */
export const ProductSuggestionsComponent = (
    productSuggest: INodeProps,
    ulProduct: INodeProps,
    props: ISearchProps<ISearchData>,
    extensionProperties: (ISearchProductItemExtensionProps | undefined)[] | undefined,
    productSuggestions?: ISearchProductViewProps,
    isLoadingAutoSuggest?: boolean,
    isLoadingNode?: React.ReactNode
): JSX.Element => {
    const liProduct: INodeProps = {
        tag: 'li',
        className: 'msc-no-result-content'
    };
    return productSuggestions ? (
        <Node {...productSuggest}>
            {renderProductSuggestionsTitle(props.resources.productSuggestionHeading)}
            <Node {...ulProduct}>
                {isLoadingAutoSuggest && isLoadingNode}
                {!isLoadingAutoSuggest &&
                    productSuggestions.items.map((item, index) => {
                        const uomPrice = extensionProperties && extensionProperties.find(p => p?.uomId == item.id);
                        return (
                            <Node {...item.LiProduct} key={item.id ?? index}>
                                <Node {...item.AProduct}>
                                    {item.thumbnail}
                                    {item.text}
                                    {item.price}
                                    {!!uomPrice && renderUomPrice(props.context, item, uomPrice)}
                                </Node>
                            </Node>
                        );
                    })}
            </Node>
        </Node>
    ) : (
        <Node {...productSuggest}>
            {renderProductSuggestionsTitle(props.resources.productSuggestionHeading)}
            <Node {...ulProduct}>
                <Node {...liProduct}>{renderProductNotFoundResults(props)}</Node>
            </Node>
        </Node>
    );
};
